import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase/cliente'
import { PROYECTOS_INICIALES } from './datosIniciales'
import type { ProyectoPlan, PlanEtapa, PlanModulo, EstadoModuloPlan, EstadoEtapaPlan } from './tipos'

const STORAGE_KEY = 'mega_plan_proyectos_v3'
const sb: any = supabase

export function usePlanProyectos() {
  const [proyectos, setProyectos] = useState<ProyectoPlan[]>(() => {
    try {
      const guardado = localStorage.getItem(STORAGE_KEY)
      if (guardado) return JSON.parse(guardado)
    } catch {}
    return PROYECTOS_INICIALES
  })
  const [cargando] = useState(false)
  const [error] = useState<string | null>(null)

  // Escuchar cambios en vivo de otros usuarios vía Supabase Realtime
  useEffect(() => {
    try {
      const canal = sb
        .channel('plan_proyectos_realtime')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'plan_modulos' },
          (payload: any) => {
            if (payload?.eventType === 'UPDATE' && payload.new) {
              const actualizado = payload.new
              setProyectos((prev) =>
                prev.map((p) => ({
                  ...p,
                  etapas: p.etapas.map((e) => ({
                    ...e,
                    modulos: e.modulos.map((m) =>
                      m.id === actualizado.id ? { ...m, ...actualizado } : m
                    ),
                  })),
                }))
              )
            }
          }
        )
        .subscribe()

      return () => {
        void sb.removeChannel(canal)
      }
    } catch {}
  }, [])

  // Cargar de Supabase si la tabla existe en la nube
  useEffect(() => {
    async function cargarBD() {
      try {
        const { data, error: err } = await sb
          .from('proyectos_plan')
          .select('*, etapas:plan_etapas(*, modulos:plan_modulos(*, colaboradores:plan_modulos_colaboradores(colaborador_id, avance_porcentaje, completado, colaborador:colaboradores(id, nombre))))')
          .order('orden', { ascending: true })

        if (!err && data && data.length > 0) {
          const formateados: ProyectoPlan[] = data
            .sort((a: any, b: any) => (a.orden ?? 999) - (b.orden ?? 999))
            .map((p: any) => ({
            ...p,
            etapas: (p.etapas || [])
              .sort((a: any, b: any) => a.orden - b.orden)
              .map((e: any) => ({
                ...e,
                modulos: (e.modulos || []).sort((a: any, b: any) => a.orden - b.orden).map((m: any) => ({
                ...m,
                elementos: Array.isArray(m.elementos) ? m.elementos : [],
                alistamiento_estado: m.alistamiento_estado || 'sin_alistar',
                alistamiento_fecha: m.alistamiento_fecha || null,
                aprobado_por: m.aprobado_por || null,
                aprobado_en: m.aprobado_en || null,
                fecha_inicio: m.fecha_inicio || null,
                fecha_fin: m.fecha_fin || null,
                colaboradores_ids: (m.colaboradores || []).map((c: any) => c.colaborador_id),
                colaboradores: (m.colaboradores || []).map((c: any) => ({
                  id: c.colaborador_id || c.colaborador?.id,
                  nombre: c.colaborador?.nombre || 'Técnico',
                  avance_porcentaje: Number(c.avance_porcentaje) || 0,
                  completado: Boolean(c.completado),
                })),
              })),
              })),
          }))
          setProyectos(formateados)
          localStorage.setItem(STORAGE_KEY, JSON.stringify(formateados))
        }
      } catch (e: any) {
        // Fallback local silencioso
      }
    }
    cargarBD()

    const onProyectosAct = () => {
      try {
        const guardado = localStorage.getItem(STORAGE_KEY)
        if (guardado) setProyectos(JSON.parse(guardado))
      } catch {}
    }
    window.addEventListener('mega:proyectos-plan-actualizado', onProyectosAct)
    return () => {
      window.removeEventListener('mega:proyectos-plan-actualizado', onProyectosAct)
    }
  }, [])

  function guardarLocal(actualizados: ProyectoPlan[]) {
    setProyectos(actualizados)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(actualizados))
    } catch {}
  }

  function crearProyecto(nuevo: {
    codigo: string
    nombre: string
    cliente: string
    ubicacion: string
    fecha_inicio: string
    fecha_fin: string
    presupuesto: number
    latitud?: number | null
    longitud?: number | null
    radio_geocerca_m?: number | null
  }) {
    const id = crypto.randomUUID()
    const etapaId = crypto.randomUUID()
    const moduloId = crypto.randomUUID()
    const creado: ProyectoPlan = {
      id,
      codigo: nuevo.codigo,
      nombre: nuevo.nombre,
      cliente: nuevo.cliente || null,
      ubicacion: nuevo.ubicacion || null,
      fecha_inicio: nuevo.fecha_inicio,
      fecha_fin: nuevo.fecha_fin,
      presupuesto: nuevo.presupuesto || 0,
      estado: 'propuesta',
      visible_en_matriz: false,
      notas: null,
      latitud: nuevo.latitud ?? null,
      longitud: nuevo.longitud ?? null,
      radio_geocerca_m: nuevo.radio_geocerca_m ?? 80,
      etapas: [
        {
          id: etapaId,
          proyecto_id: id,
          orden: 1,
          nombre: 'Fase 1: Preliminares y Diagnóstico',
          fecha_inicio: nuevo.fecha_inicio,
          fecha_fin: nuevo.fecha_fin,
          semanas: 2,
          paralela: false,
          estado: 'por_empezar',
          observaciones: 'Fase inicial de alistamiento.',
          requerimientos: 'Planos y permisos aprobados.',
          orden_trabajo_id: null,
          modulos: [
            {
              id: moduloId,
              etapa_id: etapaId,
              codigo: '1.1',
              nombre: 'Levantamiento de campo y especificaciones',
              descripcion: 'Revisión en sitio y verificación de planos.',
              unidad: 'GLB',
              cantidad: 1,
              valor_total: 0,
              horas: 8,
              horas_reales: 0,
              avance: 0,
              estado: 'pendiente',
              orden: 1,
              orden_trabajo_id: null,
            },
          ],
        },
      ],
    }
    const siguientes = [creado, ...proyectos]
    guardarLocal(siguientes)

    void (async () => {
      try {
        await sb.from('proyectos_plan').insert({
          id: creado.id,
          codigo: creado.codigo,
          nombre: creado.nombre,
          cliente: creado.cliente,
          ubicacion: creado.ubicacion,
          fecha_inicio: creado.fecha_inicio,
          fecha_fin: creado.fecha_fin,
          presupuesto: creado.presupuesto,
          estado: creado.estado,
          visible_en_matriz: false,
          latitud: creado.latitud,
          longitud: creado.longitud,
          radio_geocerca_m: creado.radio_geocerca_m,
        })
        await sb.from('plan_etapas').insert({
          id: etapaId,
          proyecto_id: id,
          orden: 1,
          nombre: 'Fase 1: Preliminares y Diagnóstico',
          fecha_inicio: nuevo.fecha_inicio,
          fecha_fin: nuevo.fecha_fin,
          semanas: 2,
          paralela: false,
          estado: 'por_empezar',
          observaciones: 'Fase inicial de alistamiento.',
        })
        await sb.from('plan_modulos').insert({
          id: moduloId,
          etapa_id: etapaId,
          codigo: '1.1',
          nombre: 'Levantamiento de campo y especificaciones',
          descripcion: 'Revisión en sitio y verificación de planos.',
          unidad: 'GLB',
          cantidad: 1,
          valor_total: 0,
          horas: 8,
          avance: 0,
          estado: 'pendiente',
          orden: 1,
        })
      } catch (err) {
        console.error('Error al insertar nuevo proyecto en BD:', err)
      }
    })()

    return creado
  }

  function actualizarProyecto(
    proyectoId: string,
    cambios: Partial<ProyectoPlan>
  ) {
    const actualizados = proyectos.map((p) => {
      if (p.id !== proyectoId) return p
      return { ...p, ...cambios }
    })
    guardarLocal(actualizados)

    const { etapas, ...camposBD } = cambios as any
    if (Object.keys(camposBD).length > 0) {
      sb.from('proyectos_plan')
        .update(camposBD)
        .eq('id', proyectoId)
        .then((res: any) => {
          if (res?.error) console.error('Error actualizando proyecto en Supabase:', res.error)
        })
    }
  }

  function actualizarModulo(
    proyectoId: string,
    etapaId: string,
    moduloId: string,
    cambios: Partial<PlanModulo>
  ) {
    const actualizados = proyectos.map((p) => {
      if (p.id !== proyectoId) return p
      const etapas = p.etapas.map((e) => {
        if (e.id !== etapaId) return e
        const modulos = e.modulos.map((m) => {
          if (m.id !== moduloId) return m
          

  return { ...m, ...cambios }
        })
        return { ...e, modulos }
      })
      return { ...p, etapas }
    })
    guardarLocal(actualizados)

    const { colaboradores, colaboradores_ids, ...camposBD } = cambios as any
    if (Object.keys(camposBD).length > 0) {
      void sb
        .from('plan_modulos')
        .update(camposBD)
        .eq('id', moduloId)
        .then((res: any) => {
          if (res?.error) console.error('Error actualizando plan_modulos:', res.error)
        })
    }
  }

  function eliminarModulo(
    proyectoId: string,
    etapaId: string,
    moduloId: string
  ) {
    const actualizados = proyectos.map((p) => {
      if (p.id !== proyectoId) return p
      const etapas = p.etapas.map((e) => {
        if (e.id !== etapaId) return e
        const modulos = e.modulos
          .filter((m) => m.id !== moduloId)
          .map((m, idx) => ({ ...m, orden: idx + 1 }))
        return { ...e, modulos }
      })
      return { ...p, etapas }
    })
    guardarLocal(actualizados)

    void sb.from('plan_modulos').delete().eq('id', moduloId)
  }

  function actualizarEstadoEtapa(
    proyectoId: string,
    etapaId: string,
    nuevoEstado: EstadoEtapaPlan
  ) {
    actualizarEtapa(proyectoId, etapaId, { estado: nuevoEstado })
  }

  function actualizarEtapa(
    proyectoId: string,
    etapaId: string,
    cambios: Partial<PlanEtapa>
  ) {
    const actualizados = proyectos.map((p) => {
      if (p.id !== proyectoId) return p
      const etapas = p.etapas.map((e) => {
        if (e.id !== etapaId) return e
        return { ...e, ...cambios }
      })
      return { ...p, etapas }
    })
    guardarLocal(actualizados)

    const { modulos, ...camposEtapa } = cambios as any
    if (Object.keys(camposEtapa).length > 0) {
      void sb.from('plan_etapas').update(camposEtapa).eq('id', etapaId)
    }
  }

  function agregarEtapa(
    proyectoId: string,
    datos?: Partial<Omit<PlanEtapa, 'id' | 'proyecto_id' | 'modulos'>>
  ): PlanEtapa | null {
    const proy = proyectos.find((p) => p.id === proyectoId)
    if (!proy) return null

    const etapaId = crypto.randomUUID()
    const nuevoOrden = proy.etapas.length + 1
    const semanas = Number(datos?.semanas) || 2

    let fechaInicio = proy.fecha_inicio || new Date().toISOString().slice(0, 10)
    if (proy.etapas.length > 0) {
      const ultima = proy.etapas[proy.etapas.length - 1]
      if (ultima.fecha_fin) {
        const d = new Date(ultima.fecha_fin + 'T00:00:00')
        d.setDate(d.getDate() + 1)
        fechaInicio = d.toISOString().slice(0, 10)
      }
    }
    const dFin = new Date(fechaInicio + 'T00:00:00')
    dFin.setDate(dFin.getDate() + semanas * 7)
    const fechaFin = dFin.toISOString().slice(0, 10)

    const nuevaEtapa: PlanEtapa = {
      id: etapaId,
      proyecto_id: proyectoId,
      orden: nuevoOrden,
      nombre: datos?.nombre?.trim() || `Etapa ${nuevoOrden}: Nueva fase`,
      fecha_inicio: datos?.fecha_inicio || fechaInicio,
      fecha_fin: datos?.fecha_fin || fechaFin,
      semanas,
      paralela: datos?.paralela ?? false,
      estado: (datos?.estado as EstadoEtapaPlan) || 'por_empezar',
      modulos: [],
    }

    const actualizados = proyectos.map((p) => {
      if (p.id !== proyectoId) return p
      const nuevaFechaFinProy = nuevaEtapa.fecha_fin > p.fecha_fin ? nuevaEtapa.fecha_fin : p.fecha_fin
      return {
        ...p,
        fecha_fin: nuevaFechaFinProy,
        etapas: [...p.etapas, nuevaEtapa],
      }
    })

    guardarLocal(actualizados)

    void (async () => {
      try {
        await sb.from('plan_etapas').insert({
          id: etapaId,
          proyecto_id: proyectoId,
          orden: nuevoOrden,
          nombre: nuevaEtapa.nombre,
          fecha_inicio: nuevaEtapa.fecha_inicio,
          fecha_fin: nuevaEtapa.fecha_fin,
          semanas: nuevaEtapa.semanas,
          paralela: nuevaEtapa.paralela,
          estado: nuevaEtapa.estado,
        })
      } catch (err) {
        console.error('Error insertando etapa en BD:', err)
      }
    })()

    return nuevaEtapa
  }

  function eliminarEtapa(proyectoId: string, etapaId: string) {
    const actualizados = proyectos.map((p) => {
      if (p.id !== proyectoId) return p
      const etapasFiltradas = p.etapas
        .filter((e) => e.id !== etapaId)
        .map((e, idx) => ({ ...e, orden: idx + 1 }))
      return { ...p, etapas: etapasFiltradas }
    })
    guardarLocal(actualizados)

    void sb.from('plan_etapas').delete().eq('id', etapaId)
  }

  function agregarModulo(
    proyectoId: string,
    etapaId: string,
    nuevo: {
      codigo?: string
      nombre: string
      descripcion?: string | null
      unidad: string
      cantidad: number
      valor_total: number
      horas: number
      estado: EstadoModuloPlan
    }
  ) {
    const id = `mod-${Date.now()}`
    let nuevoModuloCreado: PlanModulo | null = null

    const actualizados = proyectos.map((p) => {
      if (p.id !== proyectoId) return p
      const etapas = p.etapas.map((e) => {
        if (e.id !== etapaId) return e
        const orden = e.modulos.length + 1
        nuevoModuloCreado = {
          id,
          etapa_id: etapaId,
          codigo: nuevo.codigo?.trim() || `${e.orden}.${orden}`,
          nombre: nuevo.nombre.trim(),
          descripcion: nuevo.descripcion?.trim() || null,
          unidad: nuevo.unidad || 'UND',
          cantidad: Number(nuevo.cantidad) || 1,
          valor_total: Number(nuevo.valor_total) || 0,
          horas: Number(nuevo.horas) || 4,
          horas_reales: 0,
          avance: 0,
          estado: nuevo.estado || 'pendiente',
          orden,
          orden_trabajo_id: null,
        }
        return { ...e, modulos: [...e.modulos, nuevoModuloCreado] }
      })
      return { ...p, etapas }
    })
    guardarLocal(actualizados)

    if (nuevoModuloCreado) {
      void sb.from('plan_modulos').insert(nuevoModuloCreado)
    }
  }

  function reordenarModulos(
    proyectoId: string,
    etapaId: string,
    origenIdx: number,
    destinoIdx: number
  ) {
    if (origenIdx === destinoIdx) return

    const actualizados = proyectos.map((p) => {
      if (p.id !== proyectoId) return p
      const etapas = p.etapas.map((e) => {
        if (e.id !== etapaId) return e
        const lista = [...e.modulos]
        const [movido] = lista.splice(origenIdx, 1)
        lista.splice(destinoIdx, 0, movido)

        const reindexados = lista.map((m, idx) => ({ ...m, orden: idx + 1 }))
        return { ...e, modulos: reindexados }
      })
      return { ...p, etapas }
    })
    guardarLocal(actualizados)
  }

  function moverModulo(
    proyectoId: string,
    etapaId: string,
    moduloId: string,
    direccion: 'arriba' | 'abajo'
  ) {
    const proy = proyectos.find((p) => p.id === proyectoId)
    const etapa = proy?.etapas.find((e) => e.id === etapaId)
    if (!etapa) return

    const idx = etapa.modulos.findIndex((m) => m.id === moduloId)
    if (idx === -1) return
    const nuevoIdx = direccion === 'arriba' ? idx - 1 : idx + 1
    if (nuevoIdx < 0 || nuevoIdx >= etapa.modulos.length) return

    reordenarModulos(proyectoId, etapaId, idx, nuevoIdx)
  }

  function restaurarProyecto(proyectoRestaurado: ProyectoPlan) {
    const actualizados = proyectos.map((p) =>
      p.id === proyectoRestaurado.id ? proyectoRestaurado : p
    )
    guardarLocal(actualizados)

    void (async () => {
      try {
        const proyActual = proyectos.find((p) => p.id === proyectoRestaurado.id)
        if (proyActual) {
          const idsEtapasRestauradas = new Set(proyectoRestaurado.etapas.map((e) => e.id))
          const etapasAEliminar = proyActual.etapas
            .filter((e) => !idsEtapasRestauradas.has(e.id))
            .map((e) => e.id)
          if (etapasAEliminar.length > 0) {
            await sb.from('plan_etapas').delete().in('id', etapasAEliminar)
          }

          const modulosActuales = proyActual.etapas.flatMap((e) => e.modulos)
          const idsModulosRestaurados = new Set(
            proyectoRestaurado.etapas.flatMap((e) => e.modulos.map((m) => m.id))
          )
          const modulosAEliminar = modulosActuales
            .filter((m) => !idsModulosRestaurados.has(m.id))
            .map((m) => m.id)
          if (modulosAEliminar.length > 0) {
            await sb.from('plan_modulos').delete().in('id', modulosAEliminar)
          }
        }

        const { etapas, ...camposProy } = proyectoRestaurado as any
        if (Object.keys(camposProy).length > 0) {
          await sb.from('proyectos_plan').update(camposProy).eq('id', proyectoRestaurado.id)
        }
      } catch (err) {
        console.error('Error restaurando proyecto en Supabase:', err)
      }
    })()
  }

  function moverModuloAEtapa(
    proyectoId: string,
    moduloId: string,
    etapaOrigenId: string,
    etapaDestinoId: string
  ) {
    if (etapaOrigenId === etapaDestinoId) return

    let moduloMovido: PlanModulo | null = null

    const actualizados = proyectos.map((p) => {
      if (p.id !== proyectoId) return p

      const etapaOrigen = p.etapas.find((e) => e.id === etapaOrigenId)
      const etapaDestino = p.etapas.find((e) => e.id === etapaDestinoId)
      if (!etapaOrigen || !etapaDestino) return p

      const modOriginal = etapaOrigen.modulos.find((m) => m.id === moduloId)
      if (!modOriginal) return p

      const restantesOrigen = etapaOrigen.modulos
        .filter((m) => m.id !== moduloId)
        .map((m, idx) => ({ ...m, orden: idx + 1 }))

      const nuevoOrden = etapaDestino.modulos.length + 1
      moduloMovido = {
        ...modOriginal,
        etapa_id: etapaDestinoId,
        orden: nuevoOrden,
      }

      const modulosDestino = [...etapaDestino.modulos, moduloMovido]

      return {
        ...p,
        etapas: p.etapas.map((e) => {
          if (e.id === etapaOrigenId) return { ...e, modulos: restantesOrigen }
          if (e.id === etapaDestinoId) return { ...e, modulos: modulosDestino }
          return e
        }),
      }
    })

    guardarLocal(actualizados)

    if (moduloMovido) {
      void sb
        .from('plan_modulos')
        .update({ etapa_id: etapaDestinoId, orden: (moduloMovido as any).orden })
        .eq('id', moduloId)
    }
  }

  function moverModulosLote(
    proyectoId: string,
    modulosIds: string[],
    etapaOrigenId: string,
    etapaDestinoId: string
  ) {
    if (etapaOrigenId === etapaDestinoId || modulosIds.length === 0) return

    const actualizados = proyectos.map((p) => {
      if (p.id !== proyectoId) return p

      const etapaOrigen = p.etapas.find((e) => e.id === etapaOrigenId)
      const etapaDestino = p.etapas.find((e) => e.id === etapaDestinoId)
      if (!etapaOrigen || !etapaDestino) return p

      const setIds = new Set(modulosIds)
      const aMover = etapaOrigen.modulos.filter((m) => setIds.has(m.id))
      const restantesOrigen = etapaOrigen.modulos
        .filter((m) => !setIds.has(m.id))
        .map((m, idx) => ({ ...m, orden: idx + 1 }))

      const baseOrden = etapaDestino.modulos.length
      const movidosFormateados = aMover.map((m, idx) => ({
        ...m,
        etapa_id: etapaDestinoId,
        orden: baseOrden + idx + 1,
      }))

      const modulosDestino = [...etapaDestino.modulos, ...movidosFormateados]

      return {
        ...p,
        etapas: p.etapas.map((e) => {
          if (e.id === etapaOrigenId) return { ...e, modulos: restantesOrigen }
          if (e.id === etapaDestinoId) return { ...e, modulos: modulosDestino }
          return e
        }),
      }
    })

    guardarLocal(actualizados)
  }

  function importarProyectoDesdeBorrador(nuevo: ProyectoPlan) {
    const siguientes = [nuevo, ...proyectos]
    guardarLocal(siguientes)
    return nuevo
  }


  
  async function actualizarAvanceColaborador(
    proyectoId: string,
    etapaId: string,
    moduloId: string,
    colaboradorId: string,
    nuevoAvance: number,
    completado?: boolean
  ) {
    const actualizados: ProyectoPlan[] = proyectos.map((p) => {
      if (p.id !== proyectoId) return p
      const etapas = p.etapas.map((e) => {
        if (e.id !== etapaId) return e
        const modulos = e.modulos.map((m) => {
          if (m.id !== moduloId) return m
          const colabs = (m.colaboradores || []).map((c) =>
            c.id === colaboradorId
              ? { ...c, avance_porcentaje: nuevoAvance, completado: completado ?? (nuevoAvance >= 100) }
              : c
          )
          const totalAvance = colabs.length > 0
            ? Math.round(colabs.reduce((acc, curr) => acc + (curr.avance_porcentaje || 0), 0) / colabs.length)
            : m.avance
          const nuevoEstado: EstadoModuloPlan = totalAvance >= 100 ? 'hecho' : 'en_ejecucion'
          return {
            ...m,
            colaboradores: colabs,
            avance: totalAvance,
            estado: nuevoEstado,
          }
        })
        return { ...e, modulos }
      })
      return { ...p, etapas }
    })
    guardarLocal(actualizados)

    try {
      await sb.rpc('fn_actualizar_avance_colaborador', {
        p_modulo_id: moduloId,
        p_colaborador_id: colaboradorId,
        p_avance: nuevoAvance,
        p_completado: completado ?? (nuevoAvance >= 100),
      })
    } catch (err) {
      console.error('Fallo RPC fn_actualizar_avance_colaborador:', err)
    }
  }

  async function asignarColaboradoresModulo(
    proyectoId: string,
    etapaId: string,
    moduloId: string,
    colaboradoresIds: string[]
  ) {
    const actualizados: ProyectoPlan[] = proyectos.map((p) => {
      if (p.id !== proyectoId) return p
      const etapas = p.etapas.map((e) => {
        if (e.id !== etapaId) return e
        const modulos = e.modulos.map((m) => {
          if (m.id !== moduloId) return m
          const nuevoEstado: EstadoModuloPlan = colaboradoresIds.length > 0
            ? (m.avance >= 100 ? 'hecho' : 'en_ejecucion')
            : (m.avance >= 100 ? 'hecho' : 'pendiente')

          const existentes = m.colaboradores || []
          const mapaExistentes = new Map(existentes.map((c) => [c.id, c]))
          const nuevosColaboradores = colaboradoresIds.map((id) => {
            if (mapaExistentes.has(id)) return mapaExistentes.get(id)!
            return {
              id,
              nombre: 'Técnico asignado',
              avance_porcentaje: 0,
              completado: false,
            }
          })

          return {
            ...m,
            colaboradores_ids: colaboradoresIds,
            colaboradores: nuevosColaboradores,
            estado: nuevoEstado,
          }
        })
        return { ...e, modulos }
      })
      return { ...p, etapas }
    })
    guardarLocal(actualizados)

    try {
      const { data, error: err } = await sb.rpc('fn_asignar_colaboradores_modulo', {
        p_modulo_id: moduloId,
        p_colaboradores_ids: colaboradoresIds,
      })
      if (err) {
        console.error('Error al asignar colaboradores:', err)
      }
      return data
    } catch (err) {
      console.error('Fallo RPC fn_asignar_colaboradores_modulo:', err)
    }
  }

  function reordenarProyectos(nuevosProyectos: ProyectoPlan[]) {
    const ordenados = nuevosProyectos.map((p, idx) => ({
      ...p,
      orden: idx + 1,
    }))
    guardarLocal(ordenados)

    void Promise.all(
      ordenados.map((p) =>
        sb.from('proyectos_plan').update({ orden: p.orden }).eq('id', p.id)
      )
    ).catch((err: any) => {
      console.error('Error actualizando orden de proyectos en Supabase:', err)
    })
  }

  return {
    proyectos,
    cargando,
    error,
    crearProyecto,
    actualizarProyecto,
    actualizarModulo,
    eliminarModulo,
    actualizarEstadoEtapa,
    actualizarEtapa,
    agregarEtapa,
    eliminarEtapa,
    agregarModulo,
    reordenarModulos,
    moverModulo,
    restaurarProyecto,
    moverModuloAEtapa,
    moverModulosLote,
    importarProyectoDesdeBorrador,
    asignarColaboradoresModulo,
    actualizarAvanceColaborador,
    reordenarProyectos,
  }
}
